/**
 * The email+password auth form, extracted so it can render in two places:
 *
 *   - the standalone /auth page (variant="page"), for cold visits, and
 *   - embedded in the launch screen's card (variant="embedded"), where
 *     creating the account and running the scan are one click on one screen.
 *
 * Security-relevant behaviour (SECURITY.md §2.4): auth failures always render
 * GENERIC_AUTH_FAILURE, and reset requests always render RESET_REQUESTED_MESSAGE
 * whether or not the account exists. Never surface a distinguishing message
 * here — it would turn the form into a user-enumeration oracle.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  GENERIC_AUTH_FAILURE,
  PASSWORD_MIN_LENGTH,
  RESET_REQUESTED_MESSAGE,
  emailSchema,
  passwordSchema,
  nameSchema,
} from '@ideabrief/core';
import { authClient } from '../auth';
import { useConfig } from '../hooks/useConfig';
import { Button, Field, FormError, Mono, Notice } from './ui';
import { AuthProgress, MIN_VISIBLE_MS } from './AuthProgress';
import { Turnstile } from './Turnstile';

export type AuthMode = 'in' | 'up';
type Phase = 'idle' | 'pending' | 'verify_sent' | 'reset_sent';

interface AuthFormProps {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  /** Forgot-password sub-state, lifted so the parent's heading can follow it. */
  forgot: boolean;
  onForgotChange: (forgot: boolean) => void;
  /**
   * Called the moment a session exists — a sign-in, or a sign-up that returned
   * a token. The parent decides what happens next (navigate, or run the scan).
   */
  onAuthenticated: () => void;
  /**
   * 'signup' shows the consent checkbox on the sign-up form only (standalone
   * /auth). 'both' shows it in both modes — on the launch card the same tick
   * also covers processing the idea, and a returning user signing in must
   * agree before the scan runs.
   */
  consentScope: 'signup' | 'both';
  consentLabel: ReactNode;
  submitLabel: { in: string; up: string };
  /**
   * In-app path the reset email's link returns the user to. May carry a
   * ?next= allowlist key — Better Auth appends the token via the URL API, so
   * an existing query survives (verified against 1.6.25's redirectCallback).
   */
  resetRedirectPath: string;
  /**
   * Rendered above the form while it is idle or pending. Hidden once a
   * "check your inbox" / "reset link sent" notice takes over, exactly as the
   * standalone page used to hide its tabs and heading.
   */
  header?: ReactNode;
  /** 'page' wraps the fields in their own card; 'embedded' is frameless. */
  variant: 'page' | 'embedded';
  /** The disabled Google placeholder — standalone page only. */
  showGoogle?: boolean;
}

export function AuthForm({
  mode,
  onModeChange,
  forgot,
  onForgotChange,
  onAuthenticated,
  consentScope,
  consentLabel,
  submitLabel,
  resetRedirectPath,
  header,
  variant,
  showGoogle = false,
}: AuthFormProps) {
  const config = useConfig();
  const isSignUp = mode === 'up';
  const consentShown = !forgot && (consentScope === 'both' || isSignUp);

  const [phase, setPhase] = useState<Phase>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * Consent gets its own error rather than sharing formError, so it can render
   * AT the checkbox. A user who missed the tick was previously told so at the
   * top of the form, far from the control that needed attention.
   */
  const [consentError, setConsentError] = useState(false);
  /**
   * Set when a sign-up was rejected because the address already has an account.
   * Drives the explanatory notice on the sign-in form it hands the user to.
   */
  const [duplicateEmail, setDuplicateEmail] = useState<string | null>(null);
  /**
   * Survives exactly one `mode` change — the automatic switch to sign-in that
   * the duplicate itself causes. Without this the effect below would clear the
   * notice on the very transition that raised it, and the user would land on a
   * bare sign-in form with no idea why.
   */
  const keepDuplicateNotice = useRef(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  /**
   * False once this form has been torn down. The overlay's minimum-visible hold
   * means a submit can still be mid-flight when the parent swaps this component
   * out (the launch card replaces it with the scanning takeover), and resuming
   * afterwards would call onAuthenticated() — and therefore POST a scan — on
   * behalf of a screen the user has already left.
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Clear transient state when switching tabs so errors never leak across.
  // Consent survives on purpose: the duplicate-account handoff flips the mode
  // out from under a user who has already ticked the box, and asking again for
  // the same agreement they just gave reads as a bug.
  useEffect(() => {
    setFieldErrors({});
    setFormError(null);
    setConsentError(false);
    setPassword('');
    onForgotChange(false);
    if (keepDuplicateNotice.current) {
      keepDuplicateNotice.current = false;
    } else {
      // A deliberate tab switch means the user has moved on; drop the notice.
      setDuplicateEmail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  /**
   * Put the cursor in the password box when a duplicate sign-up hands the user
   * to this form: the email is already filled in, so the password is the only
   * thing left to type.
   *
   * Done with an effect rather than `autoFocus`, which fires on MOUNT — React
   * reuses the same <input> across the sign-up → sign-in switch, so the element
   * never remounts and autoFocus never fires. Confirmed in a browser.
   */
  useEffect(() => {
    if (duplicateEmail && !isSignUp && !forgot) {
      passwordRef.current?.focus();
    }
  }, [duplicateEmail, isSignUp, forgot]);

  /** Turnstile tokens are single-use — force a fresh one after every attempt. */
  function resetCaptcha() {
    setCaptchaToken('');
    setCaptchaNonce((n) => n + 1);
  }

  function validate(): boolean {
    const errors: Record<string, string> = {};

    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      errors.email = emailResult.error.issues[0]?.message ?? 'Check your email address.';
    }

    if (!forgot) {
      if (isSignUp) {
        const nameResult = nameSchema.safeParse(name);
        if (!nameResult.success) {
          errors.name = nameResult.error.issues[0]?.message ?? 'Enter your name.';
        }
        const pwResult = passwordSchema.safeParse(password);
        if (!pwResult.success) {
          errors.password = pwResult.error.issues[0]?.message ?? 'Check your password.';
        }
      } else if (!password) {
        // Sign-in only checks presence — applying the full policy would leak
        // which passwords could exist.
        errors.password = 'Enter your password.';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    // All three gates evaluate on EVERY submit, and each reports in its own
    // place: fields inline, consent at its checkbox, captcha at the top. They
    // used to short-circuit in sequence, so someone who missed the consent tick
    // AND the captcha fixed one, resubmitted, and was stopped again by the
    // other — a real user read that as the form throwing errors at them.
    const missingConsent = consentShown && !consent;
    const fieldsOk = validate();
    setConsentError(missingConsent);

    if (missingConsent || !fieldsOk) {
      if (missingConsent && fieldsOk) {
        // Nothing renders above the fold in this case; name the blocker.
        setFormError('Please accept the privacy policy and terms to continue.');
      }
      return;
    }

    if (!captchaToken) {
      setFormError("We're still verifying you're human — give it a moment.");
      return;
    }

    setPhase('pending');
    // Held so every exit from this handler can honour the overlay's minimum
    // visible time. A fast local response would otherwise show it for a single
    // frame, which reads as a glitch rather than as progress.
    const shownAt = Date.now();

    /**
     * Pads the wait out to MIN_VISIBLE_MS. Awaited on every path that leaves
     * the pending phase — INCLUDING the failures, where a form snapping back
     * with an error before the eye registers the overlay is the same glitch.
     */
    const holdOverlay = () => {
      const elapsed = Date.now() - shownAt;
      return elapsed >= MIN_VISIBLE_MS
        ? Promise.resolve()
        : new Promise((r) => setTimeout(r, MIN_VISIBLE_MS - elapsed));
    };

    /** True once it is no longer safe to act on this submit. */
    const abandoned = () => !mounted.current;

    try {
      if (forgot) {
        await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}${resetRedirectPath}`,
          fetchOptions: { body: { captchaToken } },
        });
        await holdOverlay();
        if (abandoned()) return;
        // Always the same outcome, regardless of whether the account exists.
        setPhase('reset_sent');
        return;
      }

      if (isSignUp) {
        const { data, error } = await authClient.signUp.email({
          name,
          email,
          password,
          // `consent` is REQUIRED by the Worker's auth guard, which rejects a
          // sign-up without it before Better Auth ever runs. Sending the
          // literal `true` (not the state variable) is deliberate: this line is
          // only reached after the consentShown gate above, and a coerced or
          // forwarded value could quietly become "false"/undefined. Removing
          // this field breaks sign-up outright rather than silently skipping
          // consent — which is the intended failure direction.
          fetchOptions: { body: { captchaToken, consent: true } },
        });
        if (error) throw error;

        // A FRESH sign-up is auto-signed-in: Better Auth 1.6.25 creates the
        // session and sets the cookie before responding (verified in
        // api/routes/sign-up.mjs — `shouldSkipAutoSignIn` is false unless
        // autoSignIn === false or requireEmailVerification is on; this config
        // has neither). So the user IS authenticated here and we carry them
        // straight on to finish what they started.
        //
        // A MASKED DUPLICATE takes the same branch: index.ts rewrites Better
        // Auth's 422 into 200 {ok:true} with NO session and NO token, so that
        // this screen cannot be used to test whether an address is registered
        // (SECURITY.md §2.4). We distinguish the two ONLY by whether a session
        // token came back — never by an error message — and the fallback below
        // is deliberately identical to what a real new user would see if their
        // session somehow failed to stick.
        const signedUpWithSession = Boolean(
          (data as { token?: string | null } | null)?.token,
        );

        await holdOverlay();
        if (abandoned()) return;

        if (signedUpWithSession) {
          onAuthenticated();
          return;
        }

        // No session: either a masked duplicate, or a genuine new account whose
        // session did not stick. Identical copy for both — it offers a sign-in
        // route without ever confirming the address exists.
        setPhase('verify_sent');
        return;
      }

      const { error } = await authClient.signIn.email({
        email,
        password,
        fetchOptions: { body: { captchaToken } },
      });
      if (error) throw error;
      await holdOverlay();
      if (abandoned()) return;
      onAuthenticated();
    } catch (err) {
      await holdOverlay();
      if (abandoned()) return;
      setPhase('idle');
      resetCaptcha();

      // The address already has an account: say so, and put the user on the
      // sign-in form with their email already filled in. The switch happens
      // here rather than leaving them to find the tab themselves — the whole
      // point of disclosing this is to shorten the path back in.
      if (isSignUp && isDuplicateAccount(err)) {
        keepDuplicateNotice.current = true;
        setDuplicateEmail(email);
        setPassword('');
        onModeChange('in');
        return;
      }

      setFormError(messageForError(err, isSignUp));
    }
  }

  if (phase === 'verify_sent' || phase === 'reset_sent') {
    return (
      <div>
        <Notice tone={phase === 'verify_sent' ? 'accent' : 'green'}>
          {phase === 'verify_sent' ? (
            <>
              <strong style={{ display: 'block', marginBottom: 6 }}>
                Check your inbox.
              </strong>
              We've emailed <strong>{email}</strong> a link to confirm your
              address.
            </>
          ) : (
            RESET_REQUESTED_MESSAGE
          )}
        </Notice>

        {/*
          Shown to EVERY sign-up that ends without a session — a real new
          account and a masked duplicate alike. That symmetry is the point: it
          gives the person who already has an account the route they need
          ("sign in instead") without the screen ever confirming that the
          address is registered, which would be the enumeration oracle
          SECURITY.md §2.4 forbids. Do not make this conditional on anything
          the server said about the address.
        */}
        {phase === 'verify_sent' && (
          <p
            style={{
              margin: '18px 0 0',
              fontSize: 13.5,
              lineHeight: 1.6,
              color: 'var(--muted)',
              textAlign: 'center',
            }}
          >
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => {
                setPhase('idle');
                onForgotChange(false);
                resetCaptcha();
                onModeChange('in');
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13.5,
                color: 'var(--acc)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Sign in instead
            </button>
          </p>
        )}

        <p style={{ margin: '18px 0 0', fontSize: 13, textAlign: 'center' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPhase('idle');
              onForgotChange(false);
              resetCaptcha();
            }}
          >
            Back to sign in
          </Button>
        </p>
      </div>
    );
  }

  const formStyle =
    variant === 'page'
      ? {
          marginTop: 24,
          border: '1px solid var(--line)',
          borderRadius: 16,
          background: 'var(--card)',
          padding: 24,
          boxShadow: 'var(--shadow-card)',
        }
      : { marginTop: 16 };

  return (
    <div>
      {/*
        The overlay is fixed to the viewport and sits ABOVE the form rather than
        replacing it. Swapping the form out would collapse the card to the
        overlay's height mid-request, so the glass would frost a page that had
        just jumped. Keeping it mounted also preserves the fields for the retry
        after a failure. `inert` stops it taking focus or clicks while covered.
      */}
      {phase === 'pending' && (
        <AuthProgress
          action={forgot ? 'reset' : isSignUp ? 'signup' : 'signin'}
        />
      )}

      {header}

      <form
        onSubmit={handleSubmit}
        style={formStyle}
        inert={phase === 'pending' ? true : undefined}
      >
        {showGoogle && !forgot && (
          <>
            {/*
              Google sign-in ships disabled: the layout and wiring are ready
              so enabling it later is a config change, not a redesign.
            */}
            <button
              type="button"
              disabled
              title="Google sign-in is coming soon"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 11,
                background: '#fff',
                border: '1px solid rgba(24,23,32,.18)',
                borderRadius: 10,
                padding: '14px 18px',
                fontSize: 15.5,
                fontWeight: 500,
                color: 'var(--muted)',
                cursor: 'not-allowed',
                opacity: 0.6,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 5,
                  border: '1px solid var(--line)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  font: "600 12px/1 var(--font-mono)",
                  flex: 'none',
                }}
              >
                G
              </span>
              Continue with Google — coming soon
            </button>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '18px 0',
              }}
            >
              <span style={{ height: 1, flex: 1, background: 'var(--line2)' }} />
              <Mono>OR</Mono>
              <span style={{ height: 1, flex: 1, background: 'var(--line2)' }} />
            </div>
          </>
        )}

        {/*
          Shown after a sign-up was rejected for an existing address. The
          user has already been moved to this sign-in form with their email
          filled in, so this explains the switch rather than announcing a
          failure — nothing went wrong, they just already have an account.
        */}
        {duplicateEmail && !forgot && (
          <div style={{ marginBottom: 16 }}>
            <Notice tone="accent">
              <strong style={{ display: 'block', marginBottom: 4 }}>
                You already have an account.
              </strong>
              <strong>{duplicateEmail}</strong> is already registered — sign
              in with your password to carry on.
            </Notice>
          </div>
        )}

        {formError && <FormError>{formError}</FormError>}

        {isSignUp && !forgot && (
          <Field
            label="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            error={fieldErrors.name}
            required
          />
        )}

        <Field
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="email"
          error={fieldErrors.email}
          required
        />

        {!forgot && (
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            ref={passwordRef}
            error={fieldErrors.password}
            hint={
              isSignUp
                ? 'At least 12 characters. A short phrase beats a complicated word.'
                : undefined
            }
            required
          />
        )}

        {consentShown && (
          <div style={{ margin: '4px 0 18px' }}>
            <label
              style={{
                display: 'flex',
                gap: 11,
                alignItems: 'flex-start',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  if (e.target.checked) {
                    setConsentError(false);
                    setFormError(null);
                  }
                }}
                aria-invalid={consentError || undefined}
                aria-describedby={consentError ? 'consent-error' : undefined}
                style={{
                  width: 17,
                  height: 17,
                  margin: '2px 0 0',
                  accentColor: 'var(--acc)',
                  flex: 'none',
                  cursor: 'pointer',
                  outline: consentError
                    ? '2px solid oklch(0.55 0.19 25)'
                    : undefined,
                  outlineOffset: 2,
                }}
              />
              <span
                style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)' }}
              >
                {consentLabel}
              </span>
            </label>
            {consentError && (
              <p
                id="consent-error"
                style={{
                  margin: '8px 0 0 28px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'oklch(0.55 0.19 25)',
                }}
              >
                Please accept the privacy policy and terms to continue.
              </p>
            )}
          </div>
        )}

        {config.turnstileSiteKey && (
          <Turnstile
            siteKey={config.turnstileSiteKey}
            onToken={setCaptchaToken}
            resetKey={captchaNonce}
          />
        )}

        <Button type="submit" full size="md">
          {forgot ? 'Email me a reset link' : submitLabel[mode]}
        </Button>

        {!isSignUp && !forgot && (
          <p style={{ margin: '14px 0 0', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => {
                onForgotChange(true);
                setFormError(null);
                setFieldErrors({});
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13,
                color: 'var(--acc)',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Forgot your password?
            </button>
          </p>
        )}

        {forgot && (
          <p style={{ margin: '14px 0 0', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => onForgotChange(false)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13,
                color: 'var(--acc)',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Back to sign in
            </button>
          </p>
        )}
      </form>

      {!forgot && (
        <p
          style={{
            margin: '18px 0 0',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          {isSignUp ? 'Already have an account?' : 'New to IdeaBrief?'}{' '}
          <button
            type="button"
            onClick={() => onModeChange(isSignUp ? 'in' : 'up')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 13,
              color: 'var(--acc)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {isSignUp ? 'Sign in' : 'Create one'}
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * Maps an auth failure to user-facing copy. Sign-in failures are deliberately
 * collapsed into one message; only rate limiting and captcha failures (which
 * reveal nothing about account existence) get specific copy.
 */
export function messageForError(err: unknown, isSignUp: boolean): string {
  const obj = typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : {};

  // The code may sit at either depth and BOTH occur in practice:
  //   - Better Auth's own failures put it top-level  → { code, status }
  //   - OUR Worker's middleware nests it under error → { error: { code, message }, status }
  // Verified by observing a real 429 from the running Worker, which arrives as
  //   { error: {code:'rate_limited', ...}, status: 429, statusText: 'Too Many Requests' }
  // Reading only the top level meant `captcha_failed` (a 400, so no status
  // match) silently fell through to the generic message.
  const nested =
    typeof obj.error === 'object' && obj.error !== null
      ? (obj.error as Record<string, unknown>)
      : {};
  const code = String(obj.code ?? nested.code ?? '');
  const status = Number(obj.status ?? 0);

  if (status === 429 || code === 'rate_limited') {
    return 'Too many attempts. Wait a moment and try again.';
  }
  if (code === 'captcha_failed') {
    return "We couldn't verify that you're human. Refresh the page and try again.";
  }

  if (isSignUp) {
    // Sign-up may fail for reasons the user can actually fix, and the generic
    // fallback hides all of them behind "check your details" — which tells
    // someone with a rejected password to re-read an email address that was
    // fine. These map the failures observed from the running Worker.
    //
    // This does NOT weaken §2.4: none of these codes reveals whether an
    // address is registered. That one fact is disclosed deliberately and
    // separately, via isDuplicateAccount() → the prefilled sign-in form.
    if (code === 'PASSWORD_TOO_SHORT') {
      return `Your password needs at least ${PASSWORD_MIN_LENGTH} characters.`;
    }
    if (code === 'PASSWORD_TOO_LONG') {
      return 'That password is too long.';
    }
    if (code === 'INVALID_EMAIL') {
      return 'That email address does not look right.';
    }
    if (code === 'consent_missing' || code === 'invalid_request') {
      // The Worker's consent gate. Reachable if the checkbox state and the
      // submitted body ever diverge; without this it reads as a mystery.
      return nestedMessage(obj) ?? 'Please accept the privacy policy and terms to continue.';
    }

    // Anything unrecognised. Log the shape — the fallback copy is deliberately
    // vague for the user, but a silent mapping miss is how "we couldn't create
    // that account" became unexplainable in the first place.
    if (code || status) {
      console.warn('[auth] unmapped sign-up failure', { code, status });
    }
    return "We couldn't create that account. Check your details and try again.";
  }

  return GENERIC_AUTH_FAILURE;
}

/** Server-supplied message, when it is safe and specific enough to show. */
function nestedMessage(obj: Record<string, unknown>): string | null {
  const nested =
    typeof obj.error === 'object' && obj.error !== null
      ? (obj.error as Record<string, unknown>)
      : {};
  const msg = obj.message ?? nested.message;
  return typeof msg === 'string' && msg.trim() ? msg : null;
}

/**
 * True when a sign-up failed because the address already has an account.
 *
 * This is surfaced to the user ON PURPOSE (SECURITY.md §2.4). It was previously
 * masked, which left a returning user with "check your inbox" and no email —
 * a dead end that happened in production. SIGN-IN failures stay uniform; only
 * this one case is disclosed.
 */
export function isDuplicateAccount(err: unknown): boolean {
  const obj =
    typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : {};
  const nested =
    typeof obj.error === 'object' && obj.error !== null
      ? (obj.error as Record<string, unknown>)
      : {};
  const code = String(obj.code ?? nested.code ?? '');
  const message = String(obj.message ?? nested.message ?? '');

  // Better Auth 1.6.25 actually sends `USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL`,
  // NOT the bare `USER_ALREADY_EXISTS` its own BASE_ERROR_CODES key suggests —
  // observed on a live 422, after an exact-equality check against the short
  // form failed to match. Prefix-match so both forms work, and keep the message
  // fallback in case the code is renamed again upstream.
  return (
    code.startsWith('USER_ALREADY_EXISTS') || /already exists/i.test(message)
  );
}
